import { DATA_ROOM_FOLDER_MAX_DEPTH } from 'src/enviroment/constants/constants'
import { DataRoomUpload, FolderUploadPlan } from 'src/types/projects'
import { folderName } from './folderName'
import { folderPathSegments } from './folderPathSegments'
import { isValidFolderSegment } from './isValidFolderSegment'
import { joinFolderPath } from './joinFolderPath'
import { splitUploadableFiles } from './splitUploadableFiles'

const IGNORED_NAMES = ['Thumbs.db', '__MACOSX']

const isIgnoredSegment = (segment: string): boolean => segment.startsWith('.') || IGNORED_NAMES.includes(segment)

/** Folder of a picked file relative to the data room target, e.g. "Contracts/2024" for "Contracts/2024/lease.pdf". */
const relativeFolderPath = (file: File): string => file.webkitRelativePath.split('/').slice(0, -1).join('/')

const depthOf = (path: string | null): number => (path ? path.split('/').length : 0)

/** Turns a picked folder (flat `webkitRelativePath` files) into everything the upload needs, checked before any request. */
export const buildFolderUploadPlan = (files: File[], targetPath: string | null): FolderUploadPlan => {
  const rootName = files[0]?.webkitRelativePath.split('/')[0] ?? ''
  // Whole subtrees under dot-folders go too, or `.git` would flood the skipped list
  const kept = files.filter((file) => !file.webkitRelativePath.split('/').some(isIgnoredSegment))
  const { valid, unsupported, oversized } = splitUploadableFiles(kept)

  const uploads: DataRoomUpload[] = valid.map((file) => ({
    file,
    folderPath: joinFolderPath(targetPath, relativeFolderPath(file)),
  }))

  const relativeFolders = [
    ...new Set(valid.flatMap((file) => folderPathSegments(relativeFolderPath(file)).map((segment) => segment.path))),
  ]
  const levelCount = Math.max(0, ...relativeFolders.map(depthOf))
  const folderPaths = Array.from({ length: levelCount }, (_, index) =>
    relativeFolders.filter((path) => depthOf(path) === index + 1).map((path) => joinFolderPath(targetPath, path)),
  )

  const invalidFolders: FolderUploadPlan['invalidFolders'] = [
    ...relativeFolders
      .filter((path) => !isValidFolderSegment(folderName(path)))
      .map((path) => ({ path, reason: 'name' as const })),
    // Only the first level past the limit is listed; flattening there fixes everything below it
    ...relativeFolders
      .filter((path) => depthOf(targetPath) + depthOf(path) === DATA_ROOM_FOLDER_MAX_DEPTH + 1)
      .map((path) => ({ path, reason: 'depth' as const })),
  ]

  return {
    rootName,
    rootPath: joinFolderPath(targetPath, rootName),
    uploads,
    folderPaths,
    skipped: { unsupported, oversized },
    invalidFolders,
    totalBytes: valid.reduce((sum, file) => sum + file.size, 0),
  }
}
